import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, Users, Send, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SubHeader } from "@/components/Brand";
import { api, errMsg, useApi } from "@/lib/api";
import { rp, fmtAcc, isValidAcc, MEMBER_TRANSFER_FEE } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PinInput } from "@/components/PinInput";
import { Fingerprint } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { bioAssert, bioCancelled, bioEnabledHere } from "@/lib/biometric";

const BANKS = ["BCA - Bank Central Asia", "BRI - Bank Rakyat Indonesia", "BNI - Bank Negara Indonesia", "Bank Mandiri", "Bank Kalbar", "BSI - Bank Syariah Indonesia", "CIMB Niaga", "Bank Danamon", "BTN", "Bank Permata", "Bank Lainnya"];

export default function Transfer() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const bio = bioEnabledHere(user);
  const { data: savings } = useApi("/savings");
  const { data: settings } = useApi("/settings");
  const fee = settings?.member_transfer_fee ?? MEMBER_TRANSFER_FEE;
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("ke") === "anggota" ? "member" : "bank");
  const [f, setF] = useState({ bank_name: "", account_no: "", recipient_name: "", target_member_no: "", amount: 0, note: "" });
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const lookup = async () => {
    const acc = f.target_member_no.replace(/\D/g, "");
    if (!isValidAcc(acc)) {
      setTarget(null);
      return toast.error("Nomor rekening tidak valid, periksa kembali 12 digit nomor rekening");
    }
    try {
      const r = await api.get(`/accounts/lookup/${acc}`);
      setTarget(r.data);
    } catch (e) {
      setTarget(null);
      toast.error(errMsg(e));
    }
  };

  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");

  const runBio = async () => {
    setBusy(true);
    setPinErr("");
    let credential;
    try {
      credential = await bioAssert("transaction", { amount: f.amount, target_account_no: target.account_no });
    } catch (e) {
      setBusy(false);
      if (!bioCancelled(e)) setPinErr(e?.response ? errMsg(e) : "Sidik jari tidak dikenali. Gunakan PIN.");
      return;
    }
    await submit(null, credential);
  };

  const submit = async (memberPin, webauthn) => {
    if (f.amount <= 0) return toast.error("Masukkan nominal transfer");
    if (mode === "member" && !target) return toast.error("Cek dan pastikan rekening tujuan");
    if (mode === "member" && !memberPin && !webauthn) {
      setPin("");
      setPinErr("");
      return setPinOpen(true);
    }
    setBusy(true);
    try {
      const body = mode === "bank"
        ? { kind: "transfer_bank", method: "saldo", amount: f.amount, bank_name: f.bank_name, account_no: f.account_no, recipient_name: f.recipient_name, note: f.note }
        : { kind: "transfer_member", method: "saldo", amount: f.amount, target_account_no: target.account_no, note: f.note, ...(webauthn ? { webauthn } : { pin: memberPin }), expected_fee: fee };
      const res = await api.post("/requests", body);
      setPinOpen(false);
      toast.dismiss();
      toast.success(mode === "member" ? `Transfer ${rp(f.amount)} ke ${target.name} berhasil` : "Permintaan transfer dikirim ke tim admin");
      navigate(mode === "member" ? `/app/transaksi/bukti/${res.data.req_id}` : "/app/transaksi");
    } catch (e) {
      if (mode === "member" && [400, 423].includes(e?.response?.status) && /PIN|sidik jari/i.test(errMsg(e))) {
        setPin("");
        setPinErr(errMsg(e));
        if (e.response.status !== 423) return;
        setPinOpen(false);
      } else {
        setPinOpen(false);
      }
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <SubHeader title={mode === "member" ? "Transfer Antar Anggota" : "Transfer ke Bank"} onBack={() => navigate("/app/transaksi")} />
      <div className="px-5 space-y-4">
        <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-sky-400 text-white rounded-2xl shadow-blue-500/30 p-4 flex justify-between items-center shadow-lg">
          <div>
            <div className="font-bold text-lg">{mode === "member" ? "Transfer Antar Anggota CU" : "Transfer via BI-FAST"}</div>
            <div className="text-xs text-blue-100">{mode === "member" ? "Langsung masuk, tanpa menunggu admin" : "Diproses oleh tim admin CU"}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-blue-100">Saldo Tersedia (sukarela)</div>
            <div className="font-bold" data-testid="transfer-available-balance">{rp(savings?.balances.sukarela)}</div>
          </div>
        </div>

        <div>
          <div className="text-sm font-semibold mb-2">Tujuan Transfer</div>
          <div className="grid grid-cols-2 gap-2">
            {[["bank", Landmark, "Ke Rekening Bank"], ["member", Users, "Ke Sesama Anggota"]].map(([k, Icon, l]) => (
              <button key={k} onClick={() => setMode(k)} data-testid={`transfer-mode-${k}`}
                className={`flex flex-col items-center gap-1 py-3 rounded-xl border text-sm font-semibold transition-colors ${mode === k ? "bg-red-600 text-white border-red-600" : "bg-white border-slate-200 text-slate-700"}`}>
                <Icon className="h-5 w-5" />{l}
              </button>
            ))}
          </div>
        </div>

        {mode === "bank" ? (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-semibold">Bank Tujuan</label>
              <Select value={f.bank_name} onValueChange={(v) => setF({ ...f, bank_name: v })}>
                <SelectTrigger className="mt-1.5 h-11 bg-white" data-testid="transfer-bank-select"><SelectValue placeholder="Pilih bank" /></SelectTrigger>
                <SelectContent>{BANKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-semibold">Nomor Rekening</label><Input data-testid="transfer-account-input" inputMode="numeric" className="mt-1.5 h-11 bg-white" value={f.account_no} onChange={set("account_no")} /></div>
            <div><label className="text-sm font-semibold">Nama Penerima</label><Input data-testid="transfer-recipient-input" className="mt-1.5 h-11 bg-white" value={f.recipient_name} onChange={set("recipient_name")} /></div>
          </div>
        ) : (
          <div>
            <label className="text-sm font-semibold">No. Rekening Anggota Tujuan</label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="transfer-member-input" inputMode="numeric" className="h-11 bg-white font-mono tracking-wider" placeholder="1000 0000 0016" maxLength={14}
                value={fmtAcc(f.target_member_no.replace(/\D/g, "").slice(0, 12))} onChange={(e) => { setF({ ...f, target_member_no: e.target.value.replace(/\D/g, "").slice(0, 12) }); setTarget(null); }} />
              <button onClick={lookup} data-testid="transfer-member-lookup" className="px-4 rounded-lg bg-blue-800 text-white hover:bg-blue-900 text-sm font-semibold flex items-center gap-1"><Search className="h-4 w-4" />Cek</button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">12 digit nomor rekening CU Tuah Talino milik penerima.</p>
            {target && (
              <div className="mt-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-2.5" data-testid="transfer-member-found">
                Penerima: <b>{target.name}</b>
                <div className="text-xs text-slate-600 font-mono">{fmtAcc(target.account_no)} • {target.product_name}</div>
              </div>
            )}
          </div>
        )}

        <div><label className="text-sm font-semibold">Nominal Transfer</label>
          <Input data-testid="transfer-amount-input" inputMode="numeric" className="mt-1.5 h-11 bg-white font-bold" placeholder="Rp 0"
            value={f.amount ? `Rp ${f.amount.toLocaleString("id-ID")}` : ""} onChange={(e) => setF({ ...f, amount: Number(e.target.value.replace(/\D/g, "")) || 0 })} />
        </div>
        <div><label className="text-sm font-semibold">Catatan (Opsional)</label><Input data-testid="transfer-note-input" className="mt-1.5 h-11 bg-white" value={f.note} onChange={set("note")} /></div>

        {mode === "member" && (
          <div className="bg-white border border-slate-200 rounded-xl p-3 text-sm space-y-1.5" data-testid="transfer-fee-summary">
            <div className="flex justify-between"><span className="text-slate-600">Nominal Transfer</span><b>{rp(f.amount)}</b></div>
            <div className="flex justify-between"><span className="text-slate-600">Biaya Layanan Sistem</span><b data-testid="transfer-fee">{rp(fee)}</b></div>
            <div className="flex justify-between border-t border-slate-100 pt-1.5"><span className="font-semibold">Total Dipotong</span><b className="text-red-600" data-testid="transfer-total">{rp(f.amount + fee)}</b></div>
          </div>
        )}

        <button onClick={() => submit()} disabled={busy} data-testid="transfer-submit-button"
          className="w-full flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold py-4 rounded-xl shadow-md transition-colors">
          <Send className="h-5 w-5" /> {busy ? "Mengirim..." : mode === "member" ? "Transfer Sekarang" : "Kirim Permintaan Transfer"}
        </button>
        <p className="text-[11px] text-center text-slate-500 pb-2">
          {mode === "member" ? `Transfer antar anggota langsung diproses. Nominal + biaya layanan sistem ${rp(fee)} dipotong dari simpanan sukarela.` : "Dana dipotong dari simpanan sukarela setelah transfer diproses oleh tim admin."}
        </p>
      </div>
      <Dialog open={pinOpen} onOpenChange={(o) => !busy && setPinOpen(o)}>
        <DialogContent className="max-w-sm" data-testid="transfer-pin-dialog">
          <DialogHeader>
            <DialogTitle className="text-center">Konfirmasi Transfer</DialogTitle>
            <DialogDescription className="text-center">
              Kirim <b className="text-slate-900">{rp(f.amount)}</b> ke <b className="text-slate-900">{target?.name}</b> ({fmtAcc(target?.account_no)}) + biaya layanan sistem {rp(fee)}. Total dipotong <b className="text-slate-900">{rp(f.amount + fee)}</b>. {bio ? "Gunakan sidik jari atau masukkan PIN Anda." : "Masukkan PIN Anda."}
            </DialogDescription>
          </DialogHeader>
          {bio && (
            <button onClick={runBio} disabled={busy} data-testid="transfer-biometric-button"
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors">
              <Fingerprint className="h-5 w-5" /> Konfirmasi dengan Sidik Jari
            </button>
          )}
          <PinInput value={pin} onChange={setPin} onComplete={(v) => submit(v)} disabled={busy} testid="transfer-pin-input" />
          {pinErr && <p className="text-sm text-center text-red-600 font-medium" data-testid="transfer-pin-error">{pinErr}</p>}
          <button onClick={() => submit(pin)} disabled={busy || pin.length !== 6} data-testid="transfer-pin-submit"
            className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl">{busy ? "Memproses..." : "Konfirmasi"}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
